/**
 * API client for user profile operations
 */

import api from '../api'

/**
 * User profile response from /api/auth/me
 */
export interface UserProfile {
  id: string // Clerk user ID
  uuid: string // Database UUID (internal_user_uuid or person_uuid)
  email: string
  user_type: 'internal' | 'external'
  first_name?: string
  last_name?: string
  role?: string
  phone_number?: string
}

/**
 * Extended user details (includes public metadata and database details)
 */
export interface ExtendedUserProfile extends UserProfile {
  public_metadata?: Record<string, any>
  internal_user_uuid?: string
  person_uuid?: string
  image_url?: string
}

/**
 * Auth audit log item
 */
export interface AuthAuditLog {
  auth_audit_uuid: string
  user_id: string
  user_type: string
  email_attempted: string
  event_type: string
  auth_method?: string
  ip_address?: string
  user_agent?: string
  device_fingerprint?: string
  geolocation?: Record<string, any>
  success: boolean
  failure_reason?: string
  timestamp: string
}

/**
 * Get current user's profile information
 */
export async function getCurrentUserProfile(): Promise<UserProfile> {
  const response = await api.get('/api/auth/me')
  return response.data
}

/**
 * Get auth audit logs for current user
 */
export async function getUserAuditLogs(
  userUuid: string,
  page: number = 1,
  pageSize: number = 5
): Promise<{
  total: number
  page: number
  page_size: number
  total_pages: number
  logs: AuthAuditLog[]
}> {
  const response = await api.get(`/api/internal-users/${userUuid}/audit-logs`, {
    params: { page, page_size: pageSize }
  })
  return response.data
}

/**
 * Update user profile (Clerk + database)
 */
export async function updateUserProfile(data: {
  first_name?: string
  last_name?: string
  email?: string
  phone_number?: string
}): Promise<{
  success: boolean
  message: string
  clerk_updated: boolean
  db_updated: boolean
  user_data?: any
}> {
  const formData = new FormData()

  if (data.first_name) formData.append('first_name', data.first_name)
  if (data.last_name) formData.append('last_name', data.last_name)
  if (data.email) formData.append('email', data.email)
  if (data.phone_number) formData.append('phone_number', data.phone_number)

  const response = await api.patch('/api/auth/me', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

/**
 * Upload user profile image to Clerk
 */
export async function uploadProfileImage(file: File): Promise<{
  success: boolean
  message: string
  image_url: string
}> {
  const formData = new FormData()
  formData.append('image', file)

  const response = await api.post('/api/auth/me/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

/**
 * Get internal user details by UUID
 */
export async function getInternalUser(uuid: string): Promise<{
  internal_user_uuid: string
  clerk_user_id: string
  email: string
  first_name?: string
  last_name?: string
  role?: string
  status: string
  created_at: string
  updated_at: string
  last_login_at?: string
}> {
  const response = await api.get(`/api/internal-users/${uuid}`)
  return response.data
}

/**
 * Get external user (person) details by UUID
 */
export async function getPerson(uuid: string): Promise<{
  person_uuid: string
  clerk_user_id?: string
  first_name?: string
  last_name?: string
  person_email?: string
  person_phone?: string
  role?: string
  status: string
  created_at: string
  updated_at: string
}> {
  const response = await api.get(`/api/people/${uuid}`)
  return response.data
}

